/**
 * Created by Rodrigo Quesada on 13/04/17.
 */
import {
    BoardsSection as BoardsSectionSource,
    BoardWithSubspecialtiesSection as BoardWithSubspecialtiesSectionSource,
    CMESection as CMESectionSource,
    DeaSection as DeaSectionSource,
    EducationSection as EducationSectionSource,
    LicensesAndCertificationsSection as LicensesAndCertificationsSectionSource,
    PersonalSection as PersonalSectionSource,
    ProfessionalLiabilitySection as ProfessionalLiabilitySectionSource,
    ProfessionalLicenseSection as ProfessionalLicenseSectionSource,
    ProfessionalLicensesSection as ProfessionalLicensesSectionSource,
    ProfileCompletion as ProfileCompletionSource,
    RepeatableBoardSection as RepeatableBoardSectionSource,
    RepeatableSection as RepeatableSectionSource,
    Section as SectionSource,
    SimpleRepeatableSection as SimpleRepeatableSectionSource,
    WorkSection as WorkSectionSource
} from '../../../domain/model/completion/profile-completion.model';

//region Other Classes
interface Section {

    readonly sectionName: string;
    readonly completed: boolean;
    readonly navigationCommands: any[];

    readonly show: boolean;
}

class SimpleSection implements Section {

    readonly sectionName: string;
    readonly completed: boolean;

    readonly show: boolean;

    constructor(source: SectionSource,
                readonly navigationCommands: any[],
                defaultSectionName: string,
                readonly cssClassName?: string,
                readonly descriptionText?: string) {

        this.sectionName = source.sectionName ? source.sectionName : defaultSectionName;
        this.completed = source.completed;
        this.show = !source.ignore;
    }
}

class RepeatableSection<S extends Section, SS extends SectionSource> extends SimpleSection {

    readonly entries?: S[];

    constructor(source: RepeatableSectionSource<SS>,
                navigationCommands: any[],
                defaultSectionName: string,
                defaultEntrySectionName: string,
                private readonly _newSection: new (SectionSource, n: any[], string) => S,
                cssClassName?: string,
                descriptionText?: string) {

        super(source, navigationCommands, defaultSectionName, cssClassName, descriptionText);
        const sourceEntries = source.entries;
        //quick fix, sorting entries in the front-end
        this.entries = Object.keys(sourceEntries).sort().map(k => sourceEntries[k])
            .map((it, i) => {
                const indexValue = i + 1;
                return new this._newSection(it, this.navigationCommands.concat([indexValue]), `${defaultEntrySectionName} ${indexValue}`);
            });
    }
}

class SimpleRepeatableSection extends RepeatableSection<Section, SectionSource> {

    constructor(source: SimpleRepeatableSectionSource,
                navigationCommands: any[],
                defaultSectionName: string,
                defaultEntrySectionName: string,
                cssClassName?: string,
                descriptionText?: string) {

        super(source, navigationCommands, defaultSectionName, defaultEntrySectionName, SimpleSection, cssClassName, descriptionText);
    }
}

class PersonalSection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: PersonalSectionSource) {
        super(source, ['', 'personal-details'], 'Personal', 'personal');
        this.entries = [
            new SimpleSection({
                    completed: source.userInfo.completed && source.practiceSpecialties.completed,
                    ignore: source.userInfo.ignore && source.practiceSpecialties.ignore
                },
                this.navigationCommands.concat(['basicInfo']),
                'Basic Info'
            ),
            new SimpleSection({
                    completed: source.contact.completed && source.address.completed,
                    ignore: source.contact.ignore && source.address.ignore
                },
                this.navigationCommands.concat(['contact']),
                'Contact Info'
            ),
            new SimpleSection(source.identification, this.navigationCommands.concat(['identification']), 'Identification')
        ];
    }
}

class EducationSection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: EducationSectionSource) {
        super(source, ['', 'education'], 'Education', 'education');
        this.entries = [
            new SimpleSection(source.ecfmg, this.navigationCommands.concat(['ecfmg']), 'ECFMG'),
            new SimpleSection(source.premedical, this.navigationCommands.concat(['premedical']), 'Premedical'),
            new SimpleSection(source.medical, this.navigationCommands.concat(['medical']), 'Medical School'),
            new SimpleRepeatableSection(source.other, this.navigationCommands.concat(['other']), 'Other Graduate Schools', 'school'),
            new SimpleRepeatableSection(source.internship, this.navigationCommands.concat(['internship']), 'Internships', 'internship'),
            new SimpleRepeatableSection(source.residency, this.navigationCommands.concat(['residency']), 'Residency', 'residency'),
            new SimpleRepeatableSection(source.fellowshipOrPreceptorship, this.navigationCommands.concat(['fellowshipOrPreceptorship']), 'Fellowships/Preceptorships', 'fellowship/preceptorship')
        ];
    }
}

class WorkSection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: WorkSectionSource) {
        super(source, ['', 'work'], 'Work History', 'work');
        this.entries = [
            new SimpleRepeatableSection(source.work, this.navigationCommands.concat(['history']), 'Employment', 'employment'),
            new SimpleSection(source.activityLogs, this.navigationCommands.concat(['activityLogs']), 'Procedure/Activity Logs'),
            new SimpleSection(source.military, this.navigationCommands.concat(['military']), 'Military'),
            new SimpleSection(source.observations, this.navigationCommands.concat(['observations']), 'Work History Gap')
        ];
    }
}

class LicensesAndCertificationsSection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: LicensesAndCertificationsSectionSource) {
        super(source, ['', 'licensesAndCertifications'], 'Licenses & Certs', 'licenses');
        this.entries = [
            new BoardsSection(source.board, this.navigationCommands),
            new ProfessionalLicensesSection(source.professionalLicenses, this.navigationCommands),
            new SimpleSection(source.inactiveLicenses, this.navigationCommands.concat(['inactive']), 'Inactive Licenses'),
            new DeaSection(source.dea, this.navigationCommands),
            new SimpleRepeatableSection(source.lifeCertifications, this.navigationCommands.concat(['lifeCertifications']), 'Life Certifications', 'certification'),
            new SimpleRepeatableSection(source.licensingExaminations, this.navigationCommands.concat(['licensingExaminations']), 'Licensing Examinations', 'examination'),
            new CMESection(source.cme, this.navigationCommands)
        ];
    }
}

class BoardsSection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: BoardsSectionSource, parentNavigationCommands: any[]) {
        super(source, parentNavigationCommands.concat(['board']), 'Board Affiliations');
        this.entries = [
            new AbmsSection(source.abmsBoard, this.navigationCommands),
            new SimpleRepeatableSection(source.abpsBoard, this.navigationCommands.concat(['abps']), 'ABPS Details', 'abps'),
            new AoaSection(source.aoaBoard, this.navigationCommands),
            new SimpleSection(source.boardAffiliationInformation, this.navigationCommands.concat(['specialtyExamination']), 'Specialty Examination')
        ];
    }
}

class BoardWithSubspecialtiesSection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: BoardWithSubspecialtiesSectionSource, navigationCommands: any[], defaultMainSectionName: string) {
        super(source.main, navigationCommands, defaultMainSectionName);
        this.entries = [
            new SimpleRepeatableSection(source.subspecialties, navigationCommands.concat(['sub']), '', 'subspecialty')
        ];
    }
}

class AbmsSection extends RepeatableSection<BoardWithSubspecialtiesSection, BoardWithSubspecialtiesSectionSource> {

    constructor(source: RepeatableBoardSectionSource, parentNavigationCommands: any[]) {
        super(source, parentNavigationCommands.concat(['abms']), 'ABMS Details', 'abms', BoardWithSubspecialtiesSection);
    }
}

class AoaSection extends RepeatableSection<BoardWithSubspecialtiesSection, BoardWithSubspecialtiesSectionSource> {

    constructor(source: RepeatableBoardSectionSource, parentNavigationCommands: any[]) {
        super(source, parentNavigationCommands.concat(['aoa']), 'AOA Details', 'aoa', BoardWithSubspecialtiesSection);
    }
}

class ProfessionalLicensesSection extends RepeatableSection<ProfessionalLicenseSection, ProfessionalLicenseSectionSource> {

    constructor(source: ProfessionalLicensesSectionSource, parentNavigationCommands: any[]) {
        super(source, parentNavigationCommands.concat(['professionalLicense']), 'Professional Licenses', 'license', ProfessionalLicenseSection);
    }
}

class ProfessionalLicenseSection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: ProfessionalLicenseSectionSource, navigationCommands: any[], defaultSectionName: string) {
        super(source.license, navigationCommands, defaultSectionName);
        this.entries = [
            new SimpleSection(source.permit, navigationCommands.concat(['permit']), 'permit')
        ];
    }
}

class DeaSection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: DeaSectionSource, parentNavigationCommands: any[]) {
        super(source, parentNavigationCommands.concat(['dea']), 'DEA Registration');
        this.entries = [
            new SimpleRepeatableSection(source.data, this.navigationCommands.concat(['entry']), '', 'dea'),
            new SimpleSection(source.info, this.navigationCommands.concat(['info']), 'Explanation')
        ];
    }
}

class CMESection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: CMESectionSource, parentNavigationCommands: any[]) {
        super(source, parentNavigationCommands.concat(['continuingMedicalEducations']), 'Continuing Medical Education');
        this.entries = [
            new SimpleRepeatableSection(source.data, this.navigationCommands, '', 'cme')
        ];
    }
}

class ProfessionalLiabilitySection extends SimpleSection {

    entries: Array<Section>;

    constructor(source: ProfessionalLiabilitySectionSource) {
        super(source, ['', 'liabilityAndDisclosure'], 'Liability and Disclosure', 'liability');
        this.entries = [
            new SimpleSection(source.malpractice, this.navigationCommands.concat(['malpractice']), 'Malpractice'),
            new SimpleSection(source.licensesAndAffiliations, this.navigationCommands.concat(['licensesAndAffiliations']), 'Licenses and Affiliation Status'),
            new SimpleSection(source.disciplinaryActions, this.navigationCommands.concat(['disciplinaryActions']), 'Disciplinary Action'),
            new SimpleSection(source.healthStatus, this.navigationCommands.concat(['healthStatus']), 'Health Status')
        ];
    }
}
//endregion

export class DashboardProfileCompletion {

    entries: Array<SimpleSection>;

    constructor(source: ProfileCompletionSource) {
        this.entries = [
            new PersonalSection(source.personal),
            new EducationSection(source.education),
            new WorkSection(source.work),
            new LicensesAndCertificationsSection(source.licensesAndCertifications),
            new ProfessionalLiabilitySection(source.professionalLiability),
            new SimpleRepeatableSection(source.references, ['', 'professionalReference'], 'References', 'reference', 'references', 'Reference section requires at least 3 complete references.'),
            new SimpleSection(source.releaseAuthorization, ['', 'releaseAuthorization'], 'Authorization', 'release-authorization')
        ];
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/dashboard/model/profile-completion.model.ts